<template>
  <div class="custom-country-input-container">
    <div class="custom-country-input-container-main">
      <Field
        ref="customCountry"
        v-slot="{ field }"
        v-model="mutatedValue"
        :name="name"
        :label="label"
        :rules="validation"
      >
        <label v-if="displayLabel">{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</label>
        <div 
          class="custom-country-input-content bordered-bottom"
          :class="{'autocompleted': valueHasBeenAutocompleted}"
        >
          <v-select
            :ref="name"
            v-bind="field"
            v-model="mutatedValue"
            class="custom-select-style"
            :placeholder="label"
            :options="mutatedDataOptions"
            :reduce="country => country.value"
            append-to-body
            :clearable="!isReadOnly && !valueHasBeenAutocompleted"
            :disabled="isReadOnly || valueHasBeenAutocompleted"
            @open="openSelect()"
          >
            <template #no-options="{ searching }">
              <template v-if="searching">
                {{ $t('errors.datas_not_found') }}
              </template>
            </template>
            <template #open-indicator>
              <img
                style="width: 1rem; height: 1rem"
                src="@/assets/icons/arrow-down.svg"
                alt="display options"
              >
            </template>
          </v-select>
          <transition name="slideleft">
            <FullScreenSelect
              v-if="showSelectorFS"
              :defaultValue="mutatedValue"
              :options="mutatedDataOptions"
              :name="label"
              :labelFields="null"
              :searchbar="true"
              :parentType="'CustomCountrySelect'"
              @fs-select-close="showSelectorFS = false"
              @selected-option="onChange"
            />
          </transition>
        </div>
        <ErrorMessage
          class="error-msg"
          :name="name"
          as="div"
        />
      </Field>
    </div>
  </div>
</template>

<script>
  /*
   * Props:
   * placeholder: optional, only if u want a disabled/selected option to be displayed as title
   * name: input name (string)
   * value: the value of the option that need to be displayed first (Number) --!! if you want a placeholder to be set this need to be undefined !!--
   * dataOptions:
   *      [
   *      {
   *        value: value of the option([String, Number]),
   *        label: label displayed of the selected (string)
   *      }
   *      ]
   */
import FullScreenSelect from '@/components/GenericComponents/Form/FullScreenSelect.vue'
import { appConfigurationStore } from '@/stores/appConfigurationStore.js'
import { store } from '@/stores/index.js'
import { mapState, mapActions } from 'pinia'
import { EventBus } from '@/event-bus'
import countriesJson from '@/mixins/CombinedCountries.js'
import { Field, ErrorMessage } from 'vee-validate'
import { autoCompleteService } from '@/services/autocomplete.service'
import selectOptions from '@/data/countries-en-GB.json'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'CustomCountrySelect',
  components: { Field, ErrorMessage, FullScreenSelect, vSelect },
  props: {
    label: String,
    validation: [String, Boolean],
    placeholder: [String, Number],
    defaultValue: {
      type: [String, Array, Object, Boolean],
      default: undefined
    },
    name: String,
    firstCountries: Array,
    displayLabel: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedValue: undefined,
      mutatedDataOptions: [],
      selectOptions,
      showSelectorFS: false,
      valueHasBeenAutocompleted: false
    }
  },
  computed: {
    ...mapState(store, ['deviceType', 'capencyWebserviceAvailable']),
    ...mapState(appConfigurationStore, ['appConfiguration']),
    isCapencyEnable() {
      return this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.enabled && this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.providerName == 'capency' && this.name == 'country'
    }
  },
  // Load json on lang change
  watch: {
    mutatedValue(newValue) {
      this.onChange(newValue)
    },
    '$i18n.locale'() {
      this.setJsonLang(this.$i18n.locale)
    }
  },
  async mounted() {
    if (this.defaultValue != null) {
      if (this.defaultValue === 'none') {
        this.mutatedValue = null
      } else {
        this.mutatedValue = this.defaultValue
        this.$emit('addData', { value: this.mutatedValue, name: this.name })
      }
    } else {
      this.mutatedValue = null
    }
    this.setJsonLang(this.$i18n.locale)

    let selectedOption = this.selectOptions.find(el => el.code === this.mutatedValue)
    if (selectedOption) {
      this.mutatedValue
        ? this.$emit('addData', { value: this.mutatedValue, name: this.name })
        : this.$emit('addData', { value: '', name: this.name })
    }

    if(this.isCapencyEnable) {
      let result = await autoCompleteService.checkWebServiceAvailability(this.mutatedValue)
      this.setCapencyWebserviceAvailable(result)
      EventBus.on('changeValue' + this.name, value => {
        if(value == '') {
          this.valueHasBeenAutocompleted = false
        } else {
          this.valueHasBeenAutocompleted = true
        }
      })
    }
  },
  beforeUnmount() {
    if(this.isCapencyEnable) {
      EventBus.off('changeValue' + this.name)
    }
  },
  methods: {
    ...mapActions(store, ['setCapencyWebserviceAvailable']),
    onChange(value) {
      this.mutatedValue = value
      this.$emit('addData', { value: this.mutatedValue, name: this.name })
      EventBus.emit('formGeneratorValueChanged', { name: this.name, value: this.mutatedValue })
      if(this.isCapencyEnable && this.capencyWebserviceAvailable) {
        this.valueHasBeenAutocompleted = false
        EventBus.emit('changeValueline1', '')
        EventBus.emit('changeValueline2', '')
        EventBus.emit('changeValueline3', '')
        EventBus.emit('changeValueregion', '')
        EventBus.emit('changeValuedistribution', '')
        EventBus.emit('changeValuepostcode', '')
        EventBus.emit('changeValuepostalcode', '')
        EventBus.emit('changeValuecity', '')
        EventBus.emit('changeValueregion', '')
      }
    },
    async setJsonLang(lang) {
      try {
        this.selectOptions = countriesJson[lang.replace('-', '_')]
        if (this.firstCountries && this.firstCountries.length > 0) {
          for (let [index, codeCountry] of this.firstCountries.entries()) {
            let countryIndex = this.selectOptions.findIndex(option => codeCountry === option.code)
            this.selectOptions.splice(index, 0, this.selectOptions.splice(countryIndex, 1)[0])
          }
        }
      } catch (e) {
        console.error(e)
      }
      this.mutatedDataOptions = this.selectOptions.map(({ name, code }) => ({
        label: name,
        value: code
      }))
    },
    openSelect() {
      if (this.deviceType !== 'desktop') {
        document.activeElement.blur()
        this.showSelectorFS = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .custom-country-input-container {
    margin: 10px 0;

    /* Tablet (portrait) */
    @media only screen and (width >= 768px) {
      margin: 20px 0;
    }

    /* Desktops and laptops */
    @media only screen and (width >= 1024px) {
      margin: 20px 0;
    }

    /* Large screens */
    @media only screen and (width >= 1824px) {
      margin: 20px 0;
    }

    .custom-country-input-container-main {
      text-align: left;

      label {
        padding-left: 12px;
        color: #777;
        font-size: 0.75rem !important;
        font-weight: bold;
      }
    }

    .custom-country-input-content {
      width: 100%;
      white-space: nowrap;
      align-items: center;
      padding-left: 0.5rem;
      margin-top: 9px;

      input {
        border: none;
        width: 100%;
      }
    }
  }
</style>

<style lang="scss">
  .custom-country-input-container {
    .custom-select-style input {
      height: 100%;
      border: none;
    }

    .vs__dropdown-toggle {
      display: flex;
      align-items: center;
    }

    .vs__selected-options {
      margin-right: 5px;
    }

    .custom-country-input-content.autocompleted {
      border-bottom: 1px #777 dotted;

      .custom-select-style * {
        padding: 0 0 1px !important;
        border: none !important;
        background-color: white;
        color: #777;
      }
    }

    .custom-select-style * {
      padding: 0 0 1px !important;
      border: none !important;
    }

    .custom-select-style.vs--disabled .vs__open-indicator {
      display: none;
    }

    .custom-select-style .vs__clear {
      fill: transparent;
      background-image: url('@/assets/icons/cancel.svg');
      background-position: center right;
      background-origin: content-box;
      background-repeat: no-repeat;
      background-size: contain;
      width: 1rem;
      height: 1rem;
      margin-right: 8px !important;
    }
  }
</style>
